import React, { useEffect, useState } from 'react';
import API from '../api';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from 'react-hot-toast';

// Timezone helper - ensure consistent dd/mm/yyyy format in GMT+7
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  return date.toLocaleString('en-GB', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const formatDate = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  return date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

export default function TransactionHistory({ cardUid = null }) {
  const { getMessage } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0], // Today
    end: new Date().toISOString().split('T')[0]    // Today
  });
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalCash: 0,
    totalQR: 0,
    totalManual: 0,
    totalTopup: 0,
    totalWIM: 0,
    countTopup: 0,
    countManualPayment: 0,
    countWIMPayment: 0,
    totalTransactions: 0,
    userId: null
  });

  const fetchUserTransactions = React.useCallback(async (userId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // RBAC: Ensure user can only access their own transactions
      if (user.id !== userId && user.role !== 'admin') {
        toast.error('Access denied: You can only view your own transactions');
        setHistory([]);
        setLoading(false);
        return;
      }

      const response = await API.get('/transactions/user-history', {
        params: {
          user_id: userId,
          start_date: dateRange.start,
          end_date: dateRange.end
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      const transactions = response.data.transactions || [];
      setHistory(transactions);
      calculateSummary(transactions, userId);
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied: Insufficient permissions');
      } else {
        toast.error('Failed to load transaction history');
      }
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.id) {
      setSummary(prev => ({ ...prev, userId: user.id }));
      // Always fetch by user ID - RBAC enforcement
      // Auto-load transactions when page opens or date range changes
      fetchUserTransactions(user.id);
    } else {
      setLoading(false);
      toast.error('User not authenticated');
    }
  }, [fetchUserTransactions]); // Re-fetch when date range changes

  const calculateSummary = (transactions, userId = null) => {
    const summaryData = {
      totalAmount: 0,
      totalCash: 0,
      totalQR: 0,
      totalManual: 0,
      totalTopup: 0,
      totalWIM: 0,
      countTopup: 0,
      countManualPayment: 0,
      countWIMPayment: 0,
      totalTransactions: transactions.length,
      userId: userId
    };

    transactions.forEach(tx => {
      const amount = parseFloat(tx.amount) || 0;
      summaryData.totalAmount += amount;

      // Group by transaction type
      const txType = (tx.transaction_type || tx.type || '').toLowerCase();
      const method = (tx.method || tx.payment_method || '').toLowerCase();
      const description = (tx.description || '').toLowerCase();

      if (txType.includes('topup') || txType.includes('top-up') || txType === 'qr_topup') {
        summaryData.totalTopup += amount;
        summaryData.countTopup++;

        // Check method field AND description for payment type
        const isCash = method.includes('cash') || description.includes('(cash)');
        const isQR = method.includes('qr') || description.includes('qr') || txType === 'qr_topup';

        if (isCash) {
          summaryData.totalCash += amount;
        } else if (isQR) {
          summaryData.totalQR += amount;
        }
      } else if (txType.includes('manual') || txType.includes('barrier')) {
        summaryData.totalManual += amount;
        summaryData.countManualPayment++;
      } else if (txType.includes('wim') || txType.includes('fine')) {
        summaryData.totalWIM += amount;
        summaryData.countWIMPayment++;
      }
    });

    setSummary(summaryData);
  };

  const handleSearch = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.id) {
      toast.error('User not authenticated');
      return;
    }

    // RBAC: Always filter by current user's ID only
    fetchUserTransactions(user.id);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=800,height=600');
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    printWindow.document.write(`
      <html>
        <head>
          <title>Transaction History Report - ${user.name || 'User'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Arial', sans-serif; 
              padding: 20px;
              font-size: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 { font-size: 20px; margin-bottom: 5px; }
            .header p { font-size: 12px; color: #666; }
            .summary {
              margin: 20px 0;
              border: 2px solid #333;
              padding: 15px;
              background: #f9f9f9;
            }
            .summary h2 { 
              font-size: 16px; 
              margin-bottom: 15px;
              border-bottom: 1px solid #666;
              padding-bottom: 5px;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }
            .summary-item {
              display: flex;
              justify-content: space-between;
              padding: 5px 0;
              border-bottom: 1px dashed #ccc;
            }
            .summary-item strong { font-weight: bold; }
            .total-row {
              background: #e3f2fd;
              padding: 10px;
              margin-top: 10px;
              font-weight: bold;
              font-size: 14px;
            }
            .signature-section {
              margin-top: 60px;
              padding-top: 30px;
              border-top: 2px solid #333;
            }
            .signature-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              margin-top: 20px;
            }
            .signature-box {
              border: 2px solid #333;
              padding: 20px;
              text-align: center;
            }
            .signature-box h3 {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 40px;
            }
            .signature-line {
              border-top: 2px solid #333;
              margin: 60px 20px 10px 20px;
            }
            .signature-label {
              font-size: 12px;
              margin-top: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #333;
              padding: 8px;
              text-align: left;
            }
            th {
              background: #333;
              color: white;
              font-weight: bold;
              font-size: 11px;
            }
            td { font-size: 10px; }
            .amount { text-align: right; font-weight: bold; }
            .status-success { color: green; }
            .status-failed { color: red; }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 10px;
              color: #666;
              border-top: 1px solid #ccc;
              padding-top: 10px;
            }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🚗 Smart Toll System - Transaction History Report</h1>
            <p>Cashier Control Checklist</p>
            <p>Generated on: ${formatDateTime(new Date())}</p>
          </div>

          <div class="info-section">
            <p><strong>User ID:</strong> ${summary.userId || 'N/A'}</p>
            <p><strong>User Name:</strong> ${user.name || 'N/A'}</p>
            <p><strong>Report Period:</strong> ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)} (GMT+7)</p>
          </div>

          <div class="summary">
            <h2>💰 Payment Summary</h2>
            <div class="summary-grid">
              <div class="summary-item">
                <span>Total Transactions:</span>
                <strong>${summary.totalTransactions}</strong>
              </div>
              <div class="summary-item">
                <span>Total Amount:</span>
                <strong>${summary.totalAmount.toLocaleString()} LAK</strong>
              </div>
              <div class="summary-item">
                <span>💵 Cash Topup:</span>
                <strong>${summary.totalCash.toLocaleString()} LAK</strong>
              </div>
              <div class="summary-item">
                <span>📱 QR Topup:</span>
                <strong>${summary.totalQR.toLocaleString()} LAK</strong>
              </div>
              <div class="summary-item">
                <span>🚧 Manual Payment:</span>
                <strong>${summary.totalManual.toLocaleString()} LAK</strong>
              </div>
              <div class="summary-item">
                <span>⚖️ WIM Payment:</span>
                <strong>${summary.totalWIM.toLocaleString()} LAK</strong>
              </div>
            </div>
            
            <div class="total-row" style="display: flex; justify-content: space-between;">
              <span>📊 GRAND TOTAL:</span>
              <span>${summary.totalAmount.toLocaleString()} LAK</span>
            </div>
          </div>

          <h2 style="margin-top: 20px; font-size: 14px;">📋 Transaction Details</h2>
          <table>
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Date/Time</th>
                <th>Type</th>
                <th>License Plate / Device ID</th>
                <th>Method</th>
                <th>Amount (LAK)</th>
                <th>Status</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${history.map(tx => `
                <tr>
                  <td>${tx.transaction_id || tx.id || 'N/A'}</td>
                  <td>${formatDateTime(tx.timestamp || tx.date_time || tx.created_at)}</td>
                  <td>${tx.transaction_type || tx.type || tx.method || 'N/A'}</td>
                  <td>${tx.license_plate || tx.card_uid || 'N/A'}</td>
                  <td>${tx.payment_method || tx.method || 'N/A'}</td>
                  <td class="amount">${parseFloat(tx.amount).toLocaleString()}</td>
                  <td class="${tx.status === 'success' ? 'status-success' : 'status-failed'}">
                    ${tx.status === 'success' ? '✅ Success' : '❌ Failed'}
                  </td>
                  <td>${tx.description || tx.notes || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr style="background: #e3f2fd; font-weight: bold;">
                <td colspan="5" style="text-align: right;">TOTAL:</td>
                <td class="amount">${summary.totalAmount.toLocaleString()}</td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>

          <div class="signature-section">
            <h2 style="text-align: center; font-size: 16px; margin-bottom: 20px;">📝 ການເຊັນຮັບຮອງ / Signature Verification</h2>
            <div class="signature-grid">
              <div class="signature-box">
                <h3>👤 ຜູ້ໃຊ້ງານ / User</h3>
                <div class="signature-line"></div>
                <p class="signature-label">Signature</p>
                <div style="margin-top: 20px;">
                  <p style="font-size: 12px;">ວັນທີ / Date: _________________</p>
                  <p style="font-size: 12px; margin-top: 5px;">ເວລາ / Time: _________________</p>
                </div>
              </div>
              <div class="signature-box">
                <h3>👨‍💼 ຜູ້ຈັດການປະຕູ / Gate Manager</h3>
                <div class="signature-line"></div>
                <p class="signature-label">Signature</p>
                <div style="margin-top: 20px;">
                  <p style="font-size: 12px;">ວັນທີ / Date: _________________</p>
                  <p style="font-size: 12px; margin-top: 5px;">ເວລາ / Time: _________________</p>
                </div>
              </div>
            </div>
          </div>

          <div class="footer">
            <p>Smart Toll System - Official Transaction Report</p>
            <p>ລາຍງານນີ້ຕ້ອງໄດ້ຮັບການເຊັນຮັບຮອງຈາກຜູ້ມີສິດ / This report must be signed by authorized personnel</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className={`${cardUid ? 'mt-6' : 'bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6'}`}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          📃 {getMessage('transactions.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {getMessage('transactions.description')}
        </p>

        {/* RBAC Notice */}
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded">
          <div className="flex items-center gap-2">
            <span className="text-blue-600 dark:text-blue-400 font-medium">🔒 Security Notice:</span>
            <span className="text-blue-700 dark:text-blue-300 text-sm">
              You can only view your own transaction history (User ID: {summary.userId || 'N/A'})
            </span>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            📅 Start Date
          </label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
            {formatDate(dateRange.start)} (GMT+7)
          </span>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            📅 End Date
          </label>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
            {formatDate(dateRange.end)} (GMT+7)
          </span>
        </div>
        <div className="flex items-end gap-2">
          <button
            onClick={handleSearch}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            🔍 {getMessage('common.search')}
          </button>
          <button
            onClick={handlePrint}
            disabled={history.length === 0}
            className="bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 rounded-lg transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Dashboard Summary Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-lg border-2 border-blue-200 dark:border-blue-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-300 font-medium">Total Amount</p>
              <p className="text-2xl font-bold text-blue-800 dark:text-blue-100">
                {summary.totalAmount.toLocaleString()}
              </p>
              <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">LAK</p>
            </div>
            <div className="text-4xl opacity-20">💰</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 rounded-lg border-2 border-green-200 dark:border-green-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 dark:text-green-300 font-medium">Cash Topup</p>
              <p className="text-2xl font-bold text-green-800 dark:text-green-100">
                {summary.totalCash.toLocaleString()}
              </p>
              <p className="text-xs text-green-500 dark:text-green-400 mt-1">{summary.countTopup} transactions</p>
            </div>
            <div className="text-4xl opacity-20">💵</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 rounded-lg border-2 border-purple-200 dark:border-purple-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 dark:text-purple-300 font-medium">QR Topup</p>
              <p className="text-2xl font-bold text-purple-800 dark:text-purple-100">
                {summary.totalQR.toLocaleString()}
              </p>
              <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">{summary.countTopup} transactions</p>
            </div>
            <div className="text-4xl opacity-20">📱</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900 dark:to-orange-800 rounded-lg border-2 border-orange-200 dark:border-orange-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 dark:text-orange-300 font-medium">Transactions</p>
              <p className="text-2xl font-bold text-orange-800 dark:text-orange-100">
                {summary.totalTransactions}
              </p>
              <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">Total count</p>
            </div>
            <div className="text-4xl opacity-20">📊</div>
          </div>
        </div>
      </div>

      {/* Additional Summary Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">🚧 Manual Payment</p>
              <p className="text-xl font-bold text-gray-800 dark:text-white">
                {summary.totalManual.toLocaleString()} LAK
              </p>
              <p className="text-xs text-gray-500 mt-1">{summary.countManualPayment} transactions</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">⚖️ WIM Payment</p>
              <p className="text-xl font-bold text-gray-800 dark:text-white">
                {summary.totalWIM.toLocaleString()} LAK
              </p>
              <p className="text-xs text-gray-500 mt-1">{summary.countWIMPayment} transactions</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900 dark:to-yellow-800 rounded-lg border-2 border-yellow-200 dark:border-yellow-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-yellow-600 dark:text-yellow-300 font-medium flex items-center gap-2">
                🔒 User ID (Locked)
              </p>
              <p className="text-xl font-bold text-yellow-800 dark:text-yellow-100">
                {summary.userId || 'N/A'}
              </p>
              <p className="text-xs text-yellow-500 dark:text-yellow-400 mt-1">
                {JSON.parse(localStorage.getItem('user') || '{}').name || 'Current user'}
              </p>
            </div>
            <div className="text-3xl opacity-30">🔒</div>
          </div>
        </div>
      </div>

      {/* Transaction History Table */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
        <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
          📃 {getMessage('transactions.history_title')}
        </h2>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p className="text-lg">📭 {getMessage('transactions.no_transactions')}</p>
            <p className="text-sm mt-2">Select a date range and click search to view transactions</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-600">
                  <th className="border border-gray-300 dark:border-gray-600 p-3 text-left text-sm font-semibold">
                    Transaction ID
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 p-3 text-left text-sm font-semibold">
                    {getMessage('transactions.table.date')}
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 p-3 text-left text-sm font-semibold">
                    Type
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 p-3 text-left text-sm font-semibold">
                    License Plate / Device ID
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 p-3 text-left text-sm font-semibold">
                    {getMessage('transactions.table.method')}
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 p-3 text-right text-sm font-semibold">
                    {getMessage('transactions.table.amount')}
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 p-3 text-center text-sm font-semibold">
                    {getMessage('transactions.table.status')}
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 p-3 text-left text-sm font-semibold">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((tx, index) => (
                  <tr key={tx.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                    <td className="border border-gray-300 dark:border-gray-600 p-3 text-sm font-mono text-blue-600 dark:text-blue-400">
                      {tx.transaction_id || tx.id || 'N/A'}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 p-3 text-sm">
                      {formatDateTime(tx.timestamp || tx.date_time || tx.created_at)}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 p-3 text-sm">
                      {tx.transaction_type || tx.type || tx.method || 'N/A'}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 p-3 text-sm font-medium">
                      {tx.license_plate || tx.card_uid || 'N/A'}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 p-3 text-sm">
                      {tx.payment_method || tx.method || 'N/A'}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 p-3 text-right font-semibold text-sm">
                      {parseFloat(tx.amount).toLocaleString()} LAK
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 p-3 text-center text-sm">
                      {tx.status === 'success' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                          ✅ {getMessage('transactions.status.success')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100">
                          ❌ {getMessage('transactions.status.failed')}
                        </span>
                      )}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 p-3 text-sm text-gray-600 dark:text-gray-300">
                      {tx.description || tx.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-blue-100 dark:bg-blue-900 font-bold">
                  <td colSpan="5" className="border border-gray-300 dark:border-gray-600 p-3 text-right text-sm">
                    📊 TOTAL ({history.length} records)
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-right font-bold text-sm text-green-700 dark:text-green-300">
                    {summary.totalAmount.toLocaleString()} LAK
                  </td>
                  <td colSpan="2" className="border border-gray-300 dark:border-gray-600 p-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
